import { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import ReactECharts from 'echarts-for-react';
import {
  CircleStackIcon,
  ServerStackIcon,
  TableCellsIcon,
  CubeIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

// Color palette for charts
const COLORS = {
  'Raw Data': '#3b82f6',      // Blue
  'Marts': '#10b981',         // Emerald
  'Wide Views': '#8b5cf6',    // Purple
  'Staging': '#f59e0b'        // Amber
};

const TYPE_ICONS = {
  'Raw Data': ServerStackIcon,
  'Marts': CubeIcon,
  'Wide Views': TableCellsIcon,
  'Staging': ArrowPathIcon
};

function StorageCard({ title, value, subtitle, icon: Icon, color = 'primary', percentage }) {
  const colorClasses = {
    primary: 'bg-primary-500/10 text-primary-400',
    emerald: 'bg-emerald-500/10 text-emerald-400',
    amber: 'bg-amber-500/10 text-amber-400',
    purple: 'bg-purple-500/10 text-purple-400',
    blue: 'bg-blue-500/10 text-blue-400'
  };
  
  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-surface-400 text-sm">{title}</p>
          <p className="text-3xl font-bold text-surface-100 mt-1">{value}</p>
          {subtitle && (
            <p className="text-surface-500 text-sm mt-1">{subtitle}</p>
          )}
          {percentage !== undefined && (
            <div className="mt-3">
              <div className="w-full bg-surface-700 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    percentage > 80 ? 'bg-red-500' : percentage > 60 ? 'bg-amber-500' : 'bg-emerald-500'
                  }`}
                  style={{ width: `${Math.min(percentage, 100)}%` }}
                />
              </div>
              <p className="text-xs text-surface-500 mt-1">{percentage.toFixed(1)}% of limit used</p>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}

function TypeBreakdownCard({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="card">
        <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
          <ChartBarIcon className="w-5 h-5 text-primary-400" />
          Storage by Type
        </h3>
        <div className="h-48 flex items-center justify-center text-surface-500">
          No data available
        </div>
      </div>
    );
  }
  
  return (
    <div className="card">
      <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
        <ChartBarIcon className="w-5 h-5 text-primary-400" />
        Storage by Type
      </h3>
      <div className="space-y-4">
        {data.map((item, i) => {
          const Icon = TYPE_ICONS[item.category] || ServerStackIcon;
          const color = COLORS[item.category] || '#64748b';
          const maxBytes = Math.max(...data.map(d => d.bytes));
          const widthPercent = maxBytes > 0 ? (item.bytes / maxBytes) * 100 : 0;
          
          return (
            <div key={i} className="group">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${color}20` }}
                  >
                    <Icon className="w-4 h-4" style={{ color }} />
                  </div>
                  <div>
                    <p className="font-medium text-surface-100">{item.category}</p>
                    <p className="text-xs text-surface-500">
                      {item.tableCount} table{item.tableCount !== 1 ? 's' : ''} • {item.rows.toLocaleString()} rows
                    </p>
                  </div>
                </div>
                <span className="font-semibold text-surface-100">{item.sizeFormatted}</span>
              </div>
              <div className="w-full bg-surface-700 rounded-full h-2 overflow-hidden">
                <div
                  className="h-2 rounded-full transition-all duration-500"
                  style={{ 
                    width: `${widthPercent}%`,
                    backgroundColor: color
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function WarehouseStorage() {
  const [storageData, setStorageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    loadStorageData();
  }, []);
  
  const loadStorageData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApi.getWarehouseStorage();
      setStorageData(response.data);
    } catch (err) {
      console.error('Failed to load warehouse storage:', err);
      setError(err.response?.data?.error || 'Failed to load warehouse storage data');
    } finally {
      setLoading(false);
    }
  };
  
  // Bar chart for storage by type
  const getTypeBarChartOptions = () => {
    if (!storageData?.byType?.length) return null;
    
    const categories = storageData.byType.map(d => d.category);
    const values = storageData.byType.map(d => d.bytes / (1024 * 1024)); // Convert to MB
    const colors = storageData.byType.map(d => COLORS[d.category] || '#64748b');
    
    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#1e293b',
        borderColor: '#334155',
        textStyle: { color: '#f1f5f9' },
        formatter: (params) => {
          const param = params[0];
          const item = storageData.byType[param.dataIndex];
          return `
            <div style="font-weight: 600;">${param.name}</div>
            <div style="margin-top: 4px;">${item.sizeFormatted}</div>
            <div style="color: #94a3b8; font-size: 12px;">${item.tableCount} tables • ${item.rows.toLocaleString()} rows</div>
          `;
        }
      },
      grid: {
        top: 20,
        right: 20,
        bottom: 40,
        left: 80
      },
      xAxis: {
        type: 'value',
        axisLine: { show: false },
        splitLine: { lineStyle: { color: '#334155' } },
        axisLabel: { 
          color: '#94a3b8',
          formatter: (value) => {
            if (value >= 1024) {
              return `${(value / 1024).toFixed(1)} GB`;
            }
            return `${value.toFixed(0)} MB`;
          }
        }
      },
      yAxis: {
        type: 'category',
        data: categories,
        axisLine: { lineStyle: { color: '#475569' } },
        axisLabel: { color: '#f1f5f9', fontWeight: 500 }
      },
      series: [{
        type: 'bar',
        data: values.map((value, i) => ({
          value,
          itemStyle: { 
            color: colors[i],
            borderRadius: [0, 4, 4, 0]
          }
        })),
        barWidth: '60%',
        label: {
          show: true,
          position: 'right',
          color: '#94a3b8',
          formatter: (params) => storageData.byType[params.dataIndex].sizeFormatted
        }
      }]
    };
  };
  
  // Pie chart for storage distribution
  const getPieChartOptions = () => {
    if (!storageData?.byType?.length) return null;
    
    return {
      tooltip: {
        trigger: 'item',
        backgroundColor: '#1e293b',
        borderColor: '#334155',
        textStyle: { color: '#f1f5f9' },
        formatter: (params) => {
          return `
            <div style="font-weight: 600;">${params.name}</div>
            <div style="margin-top: 4px;">${params.data.sizeFormatted}</div>
            <div style="color: #94a3b8; font-size: 12px;">${params.percent.toFixed(1)}% of total</div>
          `;
        }
      },
      legend: {
        orient: 'vertical',
        right: 20,
        top: 'center',
        textStyle: { color: '#94a3b8' }
      },
      series: [{
        type: 'pie',
        radius: ['45%', '75%'],
        center: ['35%', '50%'],
        avoidLabelOverlap: true,
        itemStyle: {
          borderRadius: 6,
          borderColor: '#0f172a',
          borderWidth: 2
        },
        label: {
          show: false
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 14,
            fontWeight: 'bold',
            color: '#f1f5f9'
          },
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        },
        data: storageData.byType.map(item => ({
          value: item.bytes,
          name: item.category,
          sizeFormatted: item.sizeFormatted,
          itemStyle: {
            color: COLORS[item.category] || '#64748b'
          }
        }))
      }]
    };
  };
  
  // Data source bar chart
  const getDataSourceChartOptions = () => {
    if (!storageData?.byDataSource?.length) return null;
    
    const data = storageData.byDataSource.slice(0, 10); // Top 10
    
    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#1e293b',
        borderColor: '#334155',
        textStyle: { color: '#f1f5f9' },
        axisPointer: { type: 'shadow' },
        formatter: (params) => {
          const param = params[0];
          const item = data[param.dataIndex];
          return `
            <div style="font-weight: 600;">${param.name}</div>
            <div style="margin-top: 4px;">${item.sizeFormatted}</div>
            <div style="color: #94a3b8; font-size: 12px;">${item.rows.toLocaleString()} rows</div>
          `;
        }
      },
      grid: {
        top: 20,
        right: 20,
        bottom: 60,
        left: 20
      },
      xAxis: {
        type: 'category',
        data: data.map(d => d.name),
        axisLine: { lineStyle: { color: '#475569' } },
        axisLabel: { 
          color: '#94a3b8',
          rotate: 30,
          interval: 0,
          fontSize: 11
        }
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        splitLine: { lineStyle: { color: '#334155' } },
        axisLabel: { 
          color: '#94a3b8',
          formatter: (value) => {
            if (value >= 1024 * 1024 * 1024) {
              return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`;
            } else if (value >= 1024 * 1024) {
              return `${(value / (1024 * 1024)).toFixed(0)} MB`;
            } else if (value >= 1024) {
              return `${(value / 1024).toFixed(0)} KB`;
            }
            return `${value} B`;
          }
        }
      },
      series: [{
        type: 'bar',
        data: data.map(item => ({
          value: item.bytes,
          itemStyle: { 
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: '#3b82f6' },
                { offset: 1, color: '#1d4ed8' }
              ]
            },
            borderRadius: [4, 4, 0, 0]
          }
        })),
        barMaxWidth: 50
      }]
    };
  };
  
  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <div className="h-8 w-64 bg-surface-800 rounded animate-pulse" />
          <div className="h-4 w-48 bg-surface-800 rounded mt-2 animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="card">
              <div className="h-24 bg-surface-800 rounded animate-pulse" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card h-80 bg-surface-800 rounded animate-pulse" />
          <div className="card h-80 bg-surface-800 rounded animate-pulse" />
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Warehouse Storage</h1>
          <p className="text-surface-400 mt-1">Monitor your data warehouse space usage</p>
        </div>
        <div className="card bg-red-500/10 border-red-500/20">
          <div className="flex items-center gap-3">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-400" />
            <div>
              <p className="text-red-400 font-medium">Error loading storage data</p>
              <p className="text-red-400/70 text-sm">{error}</p>
            </div>
          </div>
          <button onClick={loadStorageData} className="btn-secondary mt-4">
            <ArrowPathIcon className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  const typeChartOptions = getTypeBarChartOptions();
  const pieChartOptions = getPieChartOptions();
  const dataSourceChartOptions = getDataSourceChartOptions();
  
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Warehouse Storage</h1>
          <p className="text-surface-400 mt-1">Monitor your data warehouse space usage</p>
        </div>
        <button 
          onClick={loadStorageData}
          className="btn-secondary"
          disabled={loading}
        >
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StorageCard
          title="Total Storage Used"
          value={storageData?.totalFormatted || '0 B'}
          subtitle={`Limit: ${storageData?.limitFormatted || '10 GB'}`}
          icon={CircleStackIcon}
          color="primary"
          percentage={storageData?.percentage}
        />
        <StorageCard
          title="Total Tables"
          value={storageData?.tableCount || 0}
          subtitle="Across all types"
          icon={TableCellsIcon}
          color="emerald"
        />
        <StorageCard
          title="Total Rows"
          value={(storageData?.totalRows || 0).toLocaleString()}
          subtitle="Across all tables"
          icon={ServerStackIcon}
          color="purple"
        />
        <StorageCard
          title="Data Types"
          value={storageData?.byType?.length || 0}
          subtitle="Raw, Marts, Views, Staging"
          icon={CubeIcon}
          color="amber"
        />
      </div>
      
      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Type Breakdown Card */}
        <TypeBreakdownCard data={storageData?.byType} />
        
        {/* Pie Chart */}
        <div className="card">
          <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
            <CircleStackIcon className="w-5 h-5 text-emerald-400" />
            Storage Distribution
          </h3>
          {pieChartOptions ? (
            <ReactECharts option={pieChartOptions} style={{ height: 280 }} />
          ) : (
            <div className="h-[280px] flex items-center justify-center text-surface-500">
              No data available
            </div>
          )}
        </div>
      </div>
      
      {/* Bar Chart - By Type */}
      <div className="card">
        <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
          <ChartBarIcon className="w-5 h-5 text-blue-400" />
          Storage by Type
        </h3>
        {typeChartOptions ? (
          <ReactECharts option={typeChartOptions} style={{ height: 200 }} />
        ) : (
          <div className="h-[200px] flex items-center justify-center text-surface-500">
            No data available
          </div>
        )}
      </div>
      
      {/* Data Sources Chart */}
      {storageData?.byDataSource?.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
            <ServerStackIcon className="w-5 h-5 text-purple-400" />
            Top Data Sources by Size
          </h3>
          <ReactECharts option={dataSourceChartOptions} style={{ height: 300 }} />
        </div>
      )}
      
      {/* Tables List */}
      {storageData?.tables?.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
            <TableCellsIcon className="w-5 h-5 text-amber-400" />
            All Tables
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-700">
                  <th className="text-left text-sm font-medium text-surface-400 pb-3 pl-2">Table Name</th>
                  <th className="text-left text-sm font-medium text-surface-400 pb-3">Type</th>
                  <th className="text-right text-sm font-medium text-surface-400 pb-3">Rows</th>
                  <th className="text-right text-sm font-medium text-surface-400 pb-3 pr-2">Size</th>
                </tr>
              </thead>
              <tbody>
                {storageData.tables.map((table, i) => {
                  const Icon = TYPE_ICONS[table.category] || ServerStackIcon;
                  const color = COLORS[table.category] || '#64748b';
                  
                  return (
                    <tr 
                      key={i} 
                      className="border-b border-surface-800 hover:bg-surface-800/50 transition-colors"
                    >
                      <td className="py-3 pl-2">
                        <div>
                          <p className="font-medium text-surface-100">{table.friendlyName}</p>
                          {table.friendlyName !== table.tableName && (
                            <p className="text-xs text-surface-500 font-mono">{table.tableName}</p>
                          )}
                        </div>
                      </td>
                      <td className="py-3">
                        <span 
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium"
                          style={{ 
                            backgroundColor: `${color}20`,
                            color
                          }}
                        >
                          <Icon className="w-3 h-3" />
                          {table.category}
                        </span>
                      </td>
                      <td className="py-3 text-right text-surface-300">
                        {table.rows.toLocaleString()}
                      </td>
                      <td className="py-3 text-right pr-2 font-medium text-surface-100">
                        {table.sizeFormatted}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {storageData.tableCount > 50 && (
            <p className="text-center text-surface-500 text-sm mt-4">
              Showing top 50 of {storageData.tableCount} tables
            </p>
          )}
        </div>
      )}
    </div>
  );
}
